// @ts-check
import { Component } from '@theme/component';
import { afterLayout } from '@theme/custom.after-layout';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} StepModel
 * @property {HTMLLIElement} el - the step <li>
 * @property {number} firstWord - index (in the flat word list) of this step's first word
 */

/**
 * Scroll-driven "light up the steps" highlight for the custom-scroll-steps section, scrubbed to
 * scroll word by word.
 *
 * One progress value 0 to N (N = total word count) is derived from the live scroll position and
 * drives all three effects: `.is-lit` per word, `.is-active` per numbered circle, and each step's
 * `--cs-fill` (0 to 1) that the CSS scales the connecting-line bar by. Live, not a high-water
 * mark, so scrolling back up reverses the whole motion.
 *
 * Progressive enhancement: the lit look is the CSS default (`--cs-fill` included), so with no JS
 * the section still reads fine. This component adds `.is-enhanced` to dim what has not been
 * reached yet, then lights it as the reader scrolls.
 *
 * @extends {Component<{ step: HTMLLIElement[] }>}
 */
class CustomScrollStepsComponent extends Component {
  #controller = new AbortController();
  /** @type {HTMLElement | null} */
  #list = null;
  /** @type {HTMLElement[]} */
  #words = [];
  /** @type {StepModel[]} parallel to #words; sorted by ascending firstWord */
  #steps = [];
  #ticking = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    // Steps / words may have been added / removed / reordered in the theme editor.
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#init();
  }

  #init() {
    const steps = this.refs.step ?? [];
    if (steps.length === 0) return;

    const list = /** @type {HTMLElement | null} */ (
      this.querySelector('.custom-scroll-steps__list')
    );
    if (!list) {
      // The <ol> always renders alongside steps, so a miss means restructured markup. Degrade to
      // the lit CSS default rather than measuring the host, which is display:contents (zero rect).
      console.warn(
        '[custom-scroll-steps] .custom-scroll-steps__list not found; skipping scroll enhancement.'
      );
      return;
    }
    this.#list = list;

    this.#words = [];
    this.#steps = [];
    for (const el of steps) {
      const words = /** @type {HTMLElement[]} */ (
        Array.from(el.querySelectorAll('.custom-scroll-steps__word'))
      );
      this.#steps.push({ el, firstWord: this.#words.length });
      this.#words.push(...words);
    }

    // With no words to light (every step left blank), enhancing would strand the circles dimmed,
    // so stay on the lit CSS default.
    if (this.#words.length === 0) {
      list.classList.remove('is-enhanced');
      return;
    }

    if (prefersReducedMotion()) {
      list.classList.remove('is-enhanced');
      for (const word of this.#words) word.classList.remove('is-lit');
      for (const { el } of this.#steps) {
        el.classList.remove('is-active');
        el.style.removeProperty('--cs-fill'); // fall back to the full-line CSS default
      }
      return;
    }

    for (const word of this.#words) word.classList.remove('is-lit');
    for (const { el } of this.#steps) {
      el.classList.remove('is-active');
      el.style.setProperty('--cs-fill', '0');
    }
    list.classList.add('is-enhanced');

    const onScroll = () => this.#requestUpdate();
    const { signal } = this.#controller;
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onScroll, { passive: true, signal });
    afterLayout(
      list,
      () => this.#measure(),
      (p) => this.#apply(p),
      { once: true, signal }
    );
  }

  #requestUpdate() {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  }

  #update() {
    this.#apply(this.#measure());
  }

  /** @returns {number | null} How far the list has travelled through its band, 0 to 1. */
  #measure() {
    const list = this.#list;
    if (!list || this.#words.length === 0) return null;

    const rect = list.getBoundingClientRect();
    const viewport = window.innerHeight;

    // p: 0 while the list top sits at `start`, 1 once it has travelled to `end`. The mobile pair
    // is the tighter band, so the heading stays in view for the whole scrub.
    const isDesktop = window.innerWidth >= 1024;
    const start = isDesktop ? viewport * 0.85 : viewport * 0.8;
    const end = isDesktop ? viewport * 0.6 : viewport * 0.72;
    const span = rect.height + (start - end);
    return span > 0 ? Math.min(Math.max((start - rect.top) / span, 0), 1) : 0;
  }

  /** @param {number | null} p */
  #apply(p) {
    if (p === null) return;

    // Live progress (not a high-water-mark): tracks the current scroll position
    // both ways, so scrolling back up lowers it and the toggles below reverse.
    const progress = p * this.#words.length;

    this.#words.forEach((word, i) => {
      word.classList.toggle('is-lit', progress >= i + 0.5);
    });

    // Bar and circle come off the same progress, so each circle lights just as the bar reaches it.
    const steps = this.#steps;
    for (const [i, { el, firstWord }] of steps.entries()) {
      el.classList.toggle('is-active', progress >= firstWord + 0.5);

      // The last step has no connecting line after it (its fill is hidden).
      const next = steps[i + 1];
      if (!next) continue;
      const span = next.firstWord - firstWord;
      const fill =
        span > 0
          ? Math.min(Math.max((progress - firstWord) / span, 0), 1)
          : progress >= firstWord
            ? 1
            : 0;
      el.style.setProperty('--cs-fill', String(fill));
    }
  }
}

if (!customElements.get('custom-scroll-steps-component')) {
  customElements.define('custom-scroll-steps-component', CustomScrollStepsComponent);
}
